import {
  compact,
  concat,
  differenceWith,
  filter,
  find,
  findKey,
  forEach,
  groupBy,
  has,
  isArray,
  isEmpty,
  isEqual,
  isUndefined,
  keys,
  map,
  reduce,
  split,
  toLower,
  union,
} from 'lodash';
import React, { ReactNode } from 'react';
import { findBestMatch } from 'string-similarity';
import { ATTR_FORMAT_TYPES } from '../constants/attributeFormats';
import {
  FilterOperators,
  FilterParameter,
  FilterTypesKeys,
  RECIPE_GROUP_ANNOTATIONS,
  RECIPE_GROUP_DEFAULTS,
  RECIPE_GROUP_FILTERS,
  RECIPE_GROUP_TRANSFORMS,
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_CODE,
  RECIPE_GROUP_TRANSFORMS_REPLICATE,
  RECIPE_GROUP_VALIDATIONS,
  RecipeGroupType,
  SchemaBrowserType,
  SchemaBrowserTypeUnion,
  SchemaEditorSampleError,
  TransformParameterTypes,
  TX_BUILDER_RECIPE_GROUPS,
  ValidationTypes,
} from '../constants/schemaEditor';
import TransformDSLParser from './TransformDSLParser';
import TransformDSLSerializer from './TransformDSLSerializer';
import UIUtils from './UIUtils';

type RecipeGroupData = object[] | object | boolean;

export interface RecipeGroup {
  type: RecipeGroupType;
  data: RecipeGroupData;
}

interface RecipeSuggestion {
  content: ReactNode;
  recipeGroup: RecipeGroup;
}

interface TxBuilderSuggestion {
  content: ReactNode;
  recipes: RecipeGroupData[];
}

interface RecipeBuilderSelectedAttrs {
  INPUT?: string[];
  OUTPUT?: string[];
  SCHEMA_SPEC?: string[];
}

export interface SchemaAttr {
  __name: string;
  __txName: string;
  __displayName: string;
  __description: string | null;
  __depth: number | null;
  __parentName?: string;
  __controlledBy?: string;
  __type: string;
  __format: string | null;
  __formatDisplay: string;
  __isMetaData?: boolean;
}

interface NestedSchema {
  [attrName: string]: SchemaAttr;
}

interface Entities {
  [id: number]: any;
}

export function getSuggestions(
  transforms: Entities,
  recipeGroups: RecipeGroup[],
  selectedAttrs: RecipeBuilderSelectedAttrs,
  inputSchema: SchemaAttr[],
  inputSchemaNested: NestedSchema,
  inputMetaSchemaNested: NestedSchema,
  metaDataSpec: any,
  outputSchema: SchemaAttr[],
  outputSchemaNested: NestedSchema,
  schemaSpecSchema: NestedSchema | null,
  reusableTransformIds: number[],
  publicReusableTransformIds: number[],
): RecipeSuggestion[] {
  const hasInputSelection = !isEmpty(selectedAttrs[SchemaBrowserType.INPUT]);
  const hasOutputSelection = !isEmpty(selectedAttrs[SchemaBrowserType.OUTPUT]);
  const hasSchemaSpecSelection = !isEmpty(selectedAttrs[SchemaBrowserType.SCHEMA_SPEC]);
  const hasSelection = hasInputSelection || hasOutputSelection || hasSchemaSpecSelection;
  const stringifyAttr = (attr: SchemaAttr) => toLower(`${attr.__name}:${attr.__formatDisplay}`);
  const stringifiedInputAttrs = map(inputSchema, stringifyAttr);
  const suggestions: RecipeSuggestion[] = [];

  function canAddTxBuilderSuggestions() {
    return !find(
      recipeGroups,
      (group) =>
        group.type === RECIPE_GROUP_TRANSFORMS_CODE ||
        group.type === RECIPE_GROUP_TRANSFORMS_AGGREGATE,
    );
  }

  function addTxBuilderSuggestion(content: ReactNode, recipes: RecipeGroupData[]) {
    // if incompatible with existing recipes, don't add
    if (!canAddTxBuilderSuggestions()) {
      return;
    }

    // filter out the recipes which already exist
    const existingRecipeGroupData = map(
      filter(recipeGroups, { type: RECIPE_GROUP_TRANSFORMS }),
      'data',
    );
    const existingRecipes = concat(...existingRecipeGroupData);
    const filteredRecipes = differenceWith(recipes, existingRecipes, isEqual);

    if (!isEmpty(filteredRecipes)) {
      suggestions.push({
        content,
        recipeGroup: {
          type: RECIPE_GROUP_TRANSFORMS,
          data: filteredRecipes,
        },
      });
    }
  }

  function getInputAttrForSchemaSpecAttr(attrName: string) {
    try {
      const attr = schemaSpecSchema?.[attrName];

      const match = findBestMatch(attr ? stringifyAttr(attr) : attrName, stringifiedInputAttrs);

      return inputSchema[match?.bestMatchIndex]?.__name;
    } catch (error) {
      console.warn('Invalid inputs to get attr name for schema spec mapping', {
        attrName,
      });
      return attrName; // Fallback to original name
    }
  }

  function renderAttrsList(attrs: string[]) {
    return UIUtils.renderAndJoin(
      attrs,
      (attr: string, index: number) => (
        <span key={index} className="highlighted-content">
          {attr}
        </span>
      ),
      ', ',
      ' and ',
      3,
    );
  }

  // output-based suggestions
  if (!hasInputSelection && !hasSchemaSpecSelection && !isEmpty(outputSchema)) {
    const attrs = hasOutputSelection
      ? keys(selectedAttrs[SchemaBrowserType.OUTPUT])
      : [outputSchema[0].__name];

    // add format-specific tx
    const formatBasedSuggestions = getFormatBasedSuggestions(
      map(attrs, (attrName) => outputSchemaNested[attrName]),
      SchemaBrowserType.OUTPUT,
    );
    formatBasedSuggestions.forEach(({ content, recipes }) =>
      addTxBuilderSuggestion(content, recipes),
    );

    // Only add TX_BUILDER_RECIPE_GROUPS suggestions if compatible
    if (canAddTxBuilderSuggestions()) {
      // filters
      suggestions.push({
        content: (
          <div className="recipe-builder-recommendation-content">
            Filter Output based on {renderAttrsList(attrs)}
          </div>
        ),
        recipeGroup: {
          type: RECIPE_GROUP_FILTERS,
          data: [
            {
              type: TransformParameterTypes.FILTER_GROUP,
              parameters: reduce(
                attrs,
                (acc: FilterParameter[], attrName, index) => {
                  acc.push({
                    type: TransformParameterTypes.FILTER_RULE,
                    ruleOp: FilterTypesKeys.FILTER_NOT_NULL,
                    ruleVal: {
                      type: 'nullValue',
                      value: null,
                    },
                    ruleAttribute: attrName,
                  });

                  if (index < attrs.length - 1) {
                    acc.push({
                      type: TransformParameterTypes.FILTER_RULE_JOIN_OP,
                      value: FilterOperators.AND,
                    });
                  }

                  return acc;
                },
                [],
              ),
            },
          ],
        },
      });

      // defaults
      suggestions.push({
        content: (
          <div className="recipe-builder-recommendation-content">
            Set Default Values for {renderAttrsList(attrs)}
          </div>
        ),
        recipeGroup: {
          type: RECIPE_GROUP_DEFAULTS,
          data: map(attrs, (attrName) => ({
            type: 'defaults',
            parameters: {
              defaultType: 'DEFAULT_NULL',
              value: null,
            },
            __destAttr: attrName,
          })),
        },
      });
    }

    // annotate
    suggestions.push({
      content: (
        <div className="recipe-builder-recommendation-content">
          Annotate {renderAttrsList(attrs)}
        </div>
      ),
      recipeGroup: {
        type: RECIPE_GROUP_ANNOTATIONS,
        data: map(attrs, (attrName) => ({
          attr: attrName,
          description: `Description of ${attrName}`,
        })),
      },
    });

    // validate
    suggestions.push({
      content: (
        <div className="recipe-builder-recommendation-content">
          Validate that {renderAttrsList(attrs)} {attrs.length > 1 ? 'exist ' : 'exists '}
          in the Record
        </div>
      ),
      recipeGroup: {
        type: RECIPE_GROUP_VALIDATIONS,
        data: [
          {
            type: ValidationTypes.REQUIRED,
            attrs,
            options: {},
          },
        ],
      },
    });

    // remove selected attrs
    addTxBuilderSuggestion(
      <div className="recipe-builder-recommendation-content">Remove {renderAttrsList(attrs)}</div>,
      map(attrs, (attrName) => ({
        type: TransformParameterTypes.FUNCTION,
        name: 'remove',
        parameters: {
          __srcAttr: {
            type: TransformParameterTypes.ATTR_REF,
            name: attrName,
          },
        },
      })),
    );
  }

  // schema-spec-based suggestions: map schema spec attrs to the attrs of the same name/type
  if (hasSchemaSpecSelection) {
    addTxBuilderSuggestion(
      <div className="recipe-builder-recommendation-content">
        Add to Output: {renderAttrsList(keys(selectedAttrs[SchemaBrowserType.SCHEMA_SPEC]))}
      </div>,
      map(selectedAttrs[SchemaBrowserType.SCHEMA_SPEC], (isSelected, attrName: string) => ({
        type: TransformParameterTypes.FUNCTION,
        name: 'eqAttr',
        parameters: {
          __srcAttr: {
            type: TransformParameterTypes.ATTR_REF,
            name: getInputAttrForSchemaSpecAttr(attrName),
          },
        },
        __destAttr: attrName,
      })),
    );
  }

  // input-based suggestions
  const allInputAttrsSelected =
    hasInputSelection &&
    inputSchema?.length !== 0 &&
    keys(selectedAttrs[SchemaBrowserType.INPUT]).length === inputSchema?.length;

  if (
    allInputAttrsSelected ||
    (!hasSelection &&
      !find(
        recipeGroups,
        (group) =>
          group.type === RECIPE_GROUP_TRANSFORMS_CODE ||
          group.type === RECIPE_GROUP_TRANSFORMS_AGGREGATE ||
          group.type === RECIPE_GROUP_TRANSFORMS_REPLICATE,
      ))
  ) {
    // replicate tx suggestion
    suggestions.push({
      content: (
        <div className="recipe-builder-recommendation-content">
          Move without Modifying: <span className="highlighted-content">all attributes</span>
        </div>
      ),
      recipeGroup: {
        type: RECIPE_GROUP_TRANSFORMS_REPLICATE,
        data: true,
      },
    });
  }

  if (
    !hasOutputSelection &&
    !hasSchemaSpecSelection &&
    !isEmpty(inputSchema) &&
    !find(
      recipeGroups,
      (group) =>
        group.type === RECIPE_GROUP_TRANSFORMS_CODE ||
        group.type === RECIPE_GROUP_TRANSFORMS_AGGREGATE,
    )
  ) {
    // move attributes without modification
    let attrsToMove: string[] = [];

    if (hasInputSelection) {
      const selectedAttrNames = keys(selectedAttrs[SchemaBrowserType.INPUT]);
      const groupedSelectedAttrNames = groupBy(selectedAttrNames, (attrName) =>
        has(inputMetaSchemaNested, attrName),
      );
      attrsToMove = groupedSelectedAttrNames.false;
      const metaDataAttrs = filter(
        groupedSelectedAttrNames.true,
        (attrName) => metaDataSpec[`NX_${attrName}`],
      ) as string[];

      if (!isEmpty(metaDataAttrs)) {
        addTxBuilderSuggestion(
          <div className="recipe-builder-recommendation-content">
            Move without Modifying: {renderAttrsList(metaDataAttrs)}
          </div>,
          map(metaDataAttrs, (attrName) => ({
            type: 'function',
            name: 'eqAttr',
            parameters: {
              __srcAttr: {
                type: TransformParameterTypes.SYS_ATTR_REF,
                name: `NX_${attrName}`,
              },
            },
            __destAttr: `NX_${attrName}`,
          })),
        );
      }
    }

    if (isEmpty(attrsToMove)) {
      attrsToMove = [inputSchema[0].__name];
    }

    if (!allInputAttrsSelected) {
      addTxBuilderSuggestion(
        <div className="recipe-builder-recommendation-content">
          Move without Modifying: {renderAttrsList(attrsToMove)}
        </div>,
        map(attrsToMove, (attrName) => ({
          type: TransformParameterTypes.FUNCTION,
          name: 'eqAttr',
          parameters: {
            __srcAttr: {
              type: TransformParameterTypes.ATTR_REF,
              name: attrName,
            },
          },
          __destAttr: attrName,
        })),
      );
    }

    // add format-specific tx
    const formatBasedSuggestions = getFormatBasedSuggestions(
      map(attrsToMove, (attrName) => inputSchemaNested[attrName]),
      SchemaBrowserType.INPUT,
    );
    formatBasedSuggestions.forEach(({ content, recipes }) =>
      addTxBuilderSuggestion(content, recipes),
    );
  }

  if (
    !hasSelection &&
    !find(
      recipeGroups,
      (group) =>
        group.type === RECIPE_GROUP_TRANSFORMS ||
        group.type === RECIPE_GROUP_TRANSFORMS_CODE ||
        group.type === RECIPE_GROUP_TRANSFORMS_AGGREGATE ||
        group.type === RECIPE_GROUP_TRANSFORMS_REPLICATE,
    )
  ) {
    // reusable tx suggestion
    if (!isEmpty(reusableTransformIds) || !isEmpty(publicReusableTransformIds)) {
      const reusableTransformId = isEmpty(reusableTransformIds)
        ? publicReusableTransformIds[0]
        : reusableTransformIds[0];
      suggestions.push({
        content: (
          <div className="recipe-builder-recommendation-content">
            Apply Reusable Transform:{' '}
            <span className="highlighted-content">{transforms[reusableTransformId]?.name}</span>
          </div>
        ),
        recipeGroup: {
          type: RECIPE_GROUP_TRANSFORMS_CODE,
          data: {
            type: 'reusable_tx',
            transformId: reusableTransformId,
          },
        },
      });
    }
  }

  // add transform DSL code to tx_builder suggestions
  return map(suggestions, (suggestion) => {
    const { content, recipeGroup } = suggestion;
    const { type, data } = recipeGroup;

    if (type === RECIPE_GROUP_TRANSFORMS) {
      return {
        content,
        recipeGroup: {
          type,
          data: map(data as object[], completeTxBuilderRecipe),
        },
      };
    }

    return suggestion;
  });
}

export function getSampleValue(samples: any, attr: SchemaAttr, sampleIndex: number) {
  const samplesByAttr = samples?.[sampleIndex]?.attrs;
  const attrName = attr.__name;
  const attrType = attr.__type;

  // if samples have been loaded
  if (samples && !isEmpty(samples)) {
    const transformError = samples[sampleIndex] && samples[sampleIndex].error;

    // if the sample record exists
    if (samplesByAttr) {
      let valueExists = has(samplesByAttr, attrName);

      // if this attr samples don't exist, check if there are other attrs with
      // this attr's name as prefix
      if (
        !valueExists &&
        attrType?.startsWith('array') &&
        findKey(samplesByAttr, (value, key) => key.startsWith(`${attrName}.`))
      ) {
        valueExists = true;
      }

      if (valueExists) {
        const fetchedSample = isUndefined(samplesByAttr[attrName]) ? '' : samplesByAttr[attrName];

        if (fetchedSample === null) {
          return {
            error: SchemaEditorSampleError.NULL_VALUE,
            value: 'null',
            transformError,
          };
        }

        if (isUndefined(fetchedSample)) {
          return {
            error: SchemaEditorSampleError.UNDEFINED_VALUE,
            value: 'undefined',
            transformError,
          };
        }

        return {
          value: isArray(fetchedSample) ? `[${fetchedSample}]` : `${fetchedSample}`,
          transformError,
        };
      }

      return { error: SchemaEditorSampleError.MISSING_VALUE, transformError };
    }

    if (sampleIndex >= samples.length) {
      return { error: SchemaEditorSampleError.FILLER_CELL, transformError };
    }

    return { error: SchemaEditorSampleError.FILTERED_OUT, transformError };
  }

  return { error: SchemaEditorSampleError.NOT_LOADED };
}

export function completeTxBuilderRecipeGroups(
  recipeGroups: RecipeGroup[],
  transforms: any,
  throwOnError = false,
) {
  // for each tx builder recipe, if the code is not present, add it; and if the
  // code is present but the parsed recipe is not, add the parsed recipe
  return map(recipeGroups, (recipeGroup) => {
    const { type, data } = recipeGroup;

    if (type === RECIPE_GROUP_TRANSFORMS) {
      return {
        ...recipeGroup,
        data: map(data as object[], (recipe) =>
          completeTxBuilderRecipe(recipe, transforms, throwOnError),
        ),
      };
    }

    return recipeGroup;
  });
}

function completeTxBuilderRecipe(recipe: any, transforms: any, throwOnError = false) {
  let { __id, code, ...rest } = recipe;

  if (!__id && window?.crypto?.randomUUID) {
    __id = window.crypto.randomUUID();
  }

  // if the code is present, add the parsed recipe
  if (code) {
    let parsedRecipe: any;

    try {
      parsedRecipe = TransformDSLParser.parse(code, transforms);
    } catch (error) {
      if (throwOnError) {
        throw error;
      }

      parsedRecipe = {};
    }

    return {
      ...rest,
      ...parsedRecipe,
      __id,
      code,
    };
  }

  // if the code is not present, add the code
  try {
    code = TransformDSLSerializer.stringify(rest);
  } catch (error) {
    if (throwOnError) {
      throw error;
    }

    code = '';
  }

  return {
    ...rest,
    __id,
    code,
  };
}

function getFormatBasedSuggestions(
  attrs: SchemaAttr[],
  side: SchemaBrowserTypeUnion,
): TxBuilderSuggestion[] {
  const attrNamesByType: { [type: string]: string[] } = {};
  const attrsByName: { [name: string]: SchemaAttr } = {};

  attrs.forEach((attr) => {
    const attrTypes = compact(split(attr.__type, ' or '));
    const attrFormats = compact(split(attr.__format, ' or '));
    attrsByName[attr.__name] = attr;

    [...attrTypes, ...attrFormats].forEach((type) => {
      if (!attrNamesByType[type]) {
        attrNamesByType[type] = [];
      }

      if (!attrNamesByType[type].includes(attr.__name)) {
        attrNamesByType[type].push(attr.__name);
      }
    });
  });

  // combine integers and numbers because they both get the same kinds of
  // suggestions
  if (attrNamesByType.integer || attrNamesByType.number) {
    attrNamesByType.number = union(attrNamesByType.number, attrNamesByType.integer);
  }

  const suggestions: TxBuilderSuggestion[] = [];

  forEach(attrNamesByType, (attrNames, type) => {
    const [attr1, attr2] = attrNames.slice(0, 3);

    switch (type) {
      case ATTR_FORMAT_TYPES.IPV4:
      case ATTR_FORMAT_TYPES.IPV6:
        suggestions.push(
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Get Country from IP: <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'country',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                },
                __destAttr: `${attr1}_country`,
              },
            ],
          },
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Get State from IP: <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'region',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                },
                __destAttr: `${attr1}_region`,
              },
            ],
          },
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Get City from IP: <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'city',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                },
                __destAttr: `${attr1}_city`,
              },
            ],
          },
        );
        break;

      case ATTR_FORMAT_TYPES.TIME:
        suggestions.push(
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Convert Timezone of <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'convertTimeZone',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                  fromTz: {
                    type: 'string',
                    value: 'America/New_York',
                  },
                  toTz: {
                    type: 'string',
                    value: 'America/Los_Angeles',
                  },
                },
                __destAttr: `${attr1}_localTime`,
              },
            ],
          },
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Extract Time from <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'extractDate',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                  componentPattern: {
                    type: 'string',
                    value: 'HH:mm',
                  },
                },
                __destAttr: `${attr1}_time`,
              },
            ],
          },
        );
        break;

      case ATTR_FORMAT_TYPES.TIMESTAMP:
        suggestions.push({
          content: (
            <div className="recipe-builder-recommendation-content">
              Convert to ISO 8601-formatted timestamp:{' '}
              <span className="highlighted-content">{attr1}</span>
            </div>
          ),
          recipes: [
            {
              type: TransformParameterTypes.FUNCTION,
              name: 'iso8601FromString',
              parameters: {
                __srcAttr: {
                  type: TransformParameterTypes.ATTR_REF,
                  name: attr1,
                },
              },
              __destAttr: `${attr1}_iso8601`,
            },
          ],
        });
        break;

      case ATTR_FORMAT_TYPES.SSN:
      case ATTR_FORMAT_TYPES.EMAIL:
        suggestions.push(
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Hash MD5: <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'md5',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                },
                __destAttr: `${attr1}_md5`,
              },
            ],
          },
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Hash SHA256: <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'sha256',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                },
                __destAttr: `${attr1}_sha256`,
              },
            ],
          },
        );
        break;

      case 'array':
        suggestions.push(
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Extract First Element of Array: <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'firstElement',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                },
                __destAttr: `${attr1}_0`,
              },
            ],
          },
          {
            content: (
              <div className="recipe-builder-recommendation-content">
                Size of Array: <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'array_size',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                },
                __destAttr: `${attr1}_size`,
              },
            ],
          },
        );
        break;

      case 'object':
        const flattenRecipes: RecipeGroupData[] = [];

        if (side === SchemaBrowserType.INPUT) {
          flattenRecipes.unshift({
            type: TransformParameterTypes.FUNCTION,
            name: 'eqAttr',
            parameters: {
              __srcAttr: {
                type: TransformParameterTypes.ATTR_REF,
                name: attr1,
              },
            },
            __destAttr: attr1,
          });
        }

        suggestions.push({
          content: (
            <div className="recipe-builder-recommendation-content">
              Hoist Object Properties: <span className="highlighted-content">{attr1}</span>
            </div>
          ),
          recipes: [
            ...flattenRecipes,
            {
              type: TransformParameterTypes.FUNCTION,
              name: 'hoist',
              parameters: {
                __srcAttr: {
                  type: TransformParameterTypes.ATTR_REF,
                  name: attr1,
                },
              },
            },
          ],
        });
        break;

      case ATTR_FORMAT_TYPES.STRING:
        // upper case / lower case
        // if this string attr has a format, don't add the case suggestions
        if (!attrsByName[attr1]?.__format) {
          suggestions.push(
            {
              content: (
                <div className="recipe-builder-recommendation-content">
                  Convert to Upper Case: <span className="highlighted-content">{attr1}</span>
                </div>
              ),
              recipes: [
                {
                  type: TransformParameterTypes.FUNCTION,
                  name: 'toUpper',
                  parameters: {
                    __srcAttr: {
                      type: TransformParameterTypes.ATTR_REF,
                      name: attr1,
                    },
                  },
                  __destAttr: `${attr1}_upper`,
                },
              ],
            },
            {
              content: (
                <div className="recipe-builder-recommendation-content">
                  Convert to Lower Case: <span className="highlighted-content">{attr1}</span>
                </div>
              ),
              recipes: [
                {
                  type: TransformParameterTypes.FUNCTION,
                  name: 'toLower',
                  parameters: {
                    __srcAttr: {
                      type: TransformParameterTypes.ATTR_REF,
                      name: attr1,
                    },
                  },
                  __destAttr: `${attr1}_lower`,
                },
              ],
            },
          );
        }

        // concat
        if (attr2) {
          suggestions.push({
            content: (
              <div className="recipe-builder-recommendation-content">
                Concatenate <span className="highlighted-content">{attr1}</span> and{' '}
                <span className="highlighted-content">{attr2}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'concatAttrs',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                  separator: {
                    type: 'string',
                    value: '-',
                  },
                  joinWithAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr2,
                  },
                },
                __destAttr: `${attr1}-${attr2}`,
              },
            ],
          });
        }
        break;

      case ATTR_FORMAT_TYPES.NUMBER:
        // multiply
        if (attr2) {
          suggestions.push({
            content: (
              <div className="recipe-builder-recommendation-content">
                Multiply <span className="highlighted-content">{attr1}</span> by{' '}
                <span className="highlighted-content">{attr2}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'multiply',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                  multiply_by: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr2,
                  },
                },
                __destAttr: `${attr1}-${attr2}-product`,
              },
            ],
          });
        } else {
          suggestions.push({
            content: (
              <div className="recipe-builder-recommendation-content">
                Multiply by 10: <span className="highlighted-content">{attr1}</span>
              </div>
            ),
            recipes: [
              {
                type: TransformParameterTypes.FUNCTION,
                name: 'multiply',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: attr1,
                  },
                  multiply_by: {
                    type: 'number',
                    value: '10',
                  },
                },
                __destAttr: `${attr1}_x10`,
              },
            ],
          });
        }
        break;
    }
  });

  // TODO: NEX-7450: temp: for nested attributes name the output attr same as
  // the input attr to work around a TransformComposer issue
  forEach(suggestions, (suggestion) => {
    suggestion.recipes = map(suggestion.recipes, (recipe: any) => {
      const {
        __destAttr,
        parameters: { __srcAttr },
      } = recipe;

      if (__srcAttr && __destAttr?.includes('.') && __destAttr !== __srcAttr.name) {
        return {
          ...recipe,
          __destAttr: __srcAttr.name,
        };
      }

      return recipe;
    });
  });

  return suggestions;
}
